import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import logoImage from '../assets/images/logo.png';
import { 
  FaTimes, 
  FaHome, 
  FaShoppingBag, 
  FaList, 
  FaFish, 
  FaLeaf,
  FaFacebook,
  FaInstagram,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import './CustomDrawer.css';

const CustomDrawer = ({ isOpen, onClose, onCategorySelect }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [categoriesExpanded, setCategoriesExpanded] = useState(false);

  useEffect(() => {
    const unsubscribeGuppies = onSnapshot(collection(db, 'guppies'), (snapshot) => {
      const guppyNames = snapshot.docs
        .map((doc) => doc.data().name)
        .filter(Boolean)
        .map((name) => ({ name: name.trim(), source: 'guppy' }));

      setCategories((prev) => {
        const existing = new Map(prev.map((item) => [item.name.toLowerCase(), item]));
        guppyNames.forEach((item) => existing.set(item.name.toLowerCase(), item));
        return Array.from(existing.values());
      });
    });

    const unsubscribePlants = onSnapshot(collection(db, 'plants'), (snapshot) => {
      const plantNames = snapshot.docs
        .map((doc) => doc.data().name)
        .filter(Boolean)
        .map((name) => ({ name: name.trim(), source: 'plant' }));

      setCategories((prev) => {
        const existing = new Map(prev.map((item) => [item.name.toLowerCase(), item]));
        plantNames.forEach((item) => existing.set(item.name.toLowerCase(), item));
        return Array.from(existing.values());
      });
    });

    return () => {
      unsubscribeGuppies();
      unsubscribePlants();
    };
  }, []);

  const guppyCategories = categories.filter((c) => c.source === 'guppy');
  const plantCategories = categories.filter((c) => c.source === 'plant');

  return (
    <div className={`drawer-overlay ${isOpen ? 'open' : ''}`} onClick={onClose}>
      <div className={`drawer-content ${isOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* Animated Background Gradient */}
        <div className="drawer-background-gradient"></div>
        
        {/* Header */}
        <div className="drawer-header">
          <div className="drawer-logo-container">
            <img 
              src={logoImage} 
              alt="Clickfish DZ Logo" 
              className="drawer-logo"
            />
            <div className="drawer-logo-glow"></div>
          </div>
          <h2 className="drawer-title">
            <span className="title-main">Clickfish</span>
            <span className="title-accent">DZ</span>
          </h2>
          <button className="close-button" onClick={onClose} aria-label="إغلاق القائمة">
            <FaTimes />
          </button>
        </div>

        <div className="drawer-divider"></div>

        {/* Navigation Items */}
        <div className="drawer-nav-section">
          <div 
            className="drawer-item" 
            onClick={(e) => {
              const ripple = e.currentTarget.querySelector('.drawer-item-ripple');
              if (ripple) {
                ripple.classList.remove('animate');
                setTimeout(() => {
                  ripple.classList.add('animate');
                }, 10);
              }
              navigate('/'); 
              onClose(); 
            }}
          >
            <div className="drawer-item-icon">
              <FaHome />
            </div>
            <span className="drawer-text">القائمة الرئيسية</span>
            <div className="drawer-item-ripple"></div>
          </div>

          <div 
            className="drawer-item" 
            onClick={(e) => {
              const ripple = e.currentTarget.querySelector('.drawer-item-ripple');
              if (ripple) {
                ripple.classList.remove('animate');
                setTimeout(() => {
                  ripple.classList.add('animate');
                }, 10);
              }
              navigate('/OrdersPage'); 
              onClose(); 
            }}
          >
            <div className="drawer-item-icon">
              <FaShoppingBag />
            </div>
            <span className="drawer-text">الطلبات</span>
            <div className="drawer-item-ripple"></div>
          </div>
        </div>

        <div className="drawer-divider"></div>

        {/* Categories Section */}
        <div className="drawer-categories-section">
          <div 
            className="drawer-item drawer-category-header"
            onClick={() => setCategoriesExpanded(!categoriesExpanded)}
          >
            <div className="drawer-item-icon">
              <FaList />
            </div>
            <span className="drawer-text">الفئات</span>
            <div className="drawer-category-arrow">
              {categoriesExpanded ? <FaChevronUp /> : <FaChevronDown />}
            </div>
          </div>

          {categoriesExpanded && (
            <div className="drawer-categories-dropdown">
              <select
                className="drawer-select"
                onChange={(e) => {
                  onCategorySelect(e.target.value);
                  onClose();
                }}
                defaultValue=""
              >
                <option value="">جميع الفئات</option>
                {guppyCategories.length > 0 && (
                  <optgroup label="🐟 أنواع السمك">
                    {guppyCategories.map((c, i) => (
                      <option key={`g-${i}`} value={c.name}>{c.name}</option>
                    ))}
                  </optgroup>
                )}
                {plantCategories.length > 0 && (
                  <optgroup label="🌿 النباتات المائية">
                    {plantCategories.map((c, i) => (
                      <option key={`p-${i}`} value={c.name}>{c.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
          )}
        </div>

        <div className="drawer-divider"></div>

        {/* Social Links Section */}
        <div className="drawer-social-section">
          <h3 className="drawer-social-title">
            <span className="social-title-icon">🌐</span>
            <span>تابعنا على</span>
          </h3>
          <div className="drawer-social-links">
            <a
              href="https://www.facebook.com/minou.amine.549436"
              target="_blank"
              rel="noopener noreferrer"
              className="drawer-social-link facebook"
            >
              <div className="social-link-icon">
                <FaFacebook />
              </div>
              <span>Facebook</span>
              <div className="social-link-ripple"></div>
            </a>
            <a
              href="https://www.instagram.com/mi__noou/"
              target="_blank"
              rel="noopener noreferrer"
              className="drawer-social-link instagram"
            >
              <div className="social-link-icon">
                <FaInstagram />
              </div>
              <span>Instagram</span>
              <div className="social-link-ripple"></div>
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="drawer-footer">
          <div className="drawer-legal-links">
            <button className="legal-link-btn" onClick={() => { navigate('/privacy'); onClose(); }}>سياسة الخصوصية</button>
            <button className="legal-link-btn" onClick={() => { navigate('/terms'); onClose(); }}>الشروط والأحكام</button>
            <button className="legal-link-btn" onClick={() => { navigate('/data-deletion'); onClose(); }}>حذف البيانات</button>
          </div>
          <p className="drawer-footer-text">
            © 2024 Clickfish DZ
          </p>
          <p className="drawer-footer-subtext">
            متجرك المفضل لأسماك الزينة
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomDrawer;